/* eslint-disable @typescript-eslint/typedef */
import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { PopupService } from '@app/core/services/popup.service';
import { TranslocoService } from '@ngneat/transloco';
import { rxActions } from '@rx-angular/state/actions';
import { TimesheetService } from '@timesheet/state/timesheet.service';
import { TimesheetState } from '@timesheet/state/timesheet.state';
import { Timesheet, TimesheetEventItem } from '@timesheet/state/timesheet.types';
import { catchError, exhaustMap, filter, map, of, switchMap, tap } from 'rxjs';

interface ITimesheetAction {
  getTimesheets: {
    queryParams?: Params;
  };
  getTimesheetById: {
    id: string;
  };
  getTimesheetByIdSuccess: {
    timesheet: Timesheet;
  };
  createTimesheet: {
    timesheet: URLSearchParams;
    activatedRoute: ActivatedRoute;
  };
  createTimesheetSuccess: {
    timesheet: Timesheet;
  };
  updateTimesheet: {
    id: string;
    timesheet: any;
  };
  updateTimesheetSuccess: {
    timesheet: Timesheet;
  };
  deleteTimesheet: {
    id: string;
    activatedRoute: ActivatedRoute;
  };
  deleteTimesheetSuccess: void;
  getTimesheetEventItems: {
    queryParams?: Params;
  };
  getTimesheetEventItemsSuccess: {
    pagination: any;
    items: TimesheetEventItem[];
  };
  updateTimesheetEvents: {
    id: string;
    timesheet: any;
  };
  updateTimesheetEventsSuccess: {
    timesheet: Timesheet;
  };
  selectDateFilter: {
    type: 'all' | '3m' | 'yd' | 'custom' | null;
  };
}

@Injectable({
  providedIn: 'root'
})
export class TimesheetActions {
  protected readonly translocoService: TranslocoService = inject(TranslocoService);

  actions = rxActions<ITimesheetAction>();

  private readonly _router = inject(Router);
  private readonly _timesheetService = inject(TimesheetService);
  private readonly _timesheetState = inject(TimesheetState);
  private readonly _popupService: PopupService = inject(PopupService);

  constructor() {
    this._subscribeToGetTimesheets$();
    this._subscribeToGetTimesheetById$();
    this._subscribeToGetTimesheetByIdSuccess$();
    this._subscribeToCreateTimesheet$();
    this._subscribeToCreateTimesheetSuccess$();
    this._subscribeToUpdateTimesheet$();
    this._subscribeToUpdateTimesheetSuccess$();
    this._subscribeToDeleteTimesheet$();
    this._subscribeToUpdateTimesheetSuccess$();
    this._subscribeToGetTimesheetEventItems$();
    this._subscribeToGetTimesheetEventItemsSuccess$();
    this._subscribeToUpdateTimesheetEvents$();
    this._subscribeToUpdateTimesheetEventsSuccess$();
    this._subscribeToSelectDateFilter$();
  }

  private _subscribeToGetTimesheets$(): void {
    type APIResponse = UnwrapReturnObs<typeof this._timesheetService.getTimesheets>;

    this.actions.getTimesheets$
      .pipe(
        switchMap((payload) =>
          this._timesheetService.getTimesheets(payload?.queryParams).pipe<APIResponse, APIResponse>(
            catchError((error) => of(error)),
            filter((response) => !(response instanceof HttpErrorResponse))
          )
        )
      )
      .subscribe({
        next: (pagination) => {
          this._timesheetState.set({
            pagination: pagination,
            timesheets: pagination.data
          });
        }
      });
  }

  private _subscribeToGetTimesheetById$(): void {
    this.actions.onGetTimesheetById(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this._timesheetService.getTimesheetById>;

        return payload$.pipe(
          switchMap((payload) =>
            this._timesheetService.getTimesheetById(payload.id).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (timesheet) => this.actions.getTimesheetByIdSuccess({ timesheet })
    );
  }

  private _subscribeToGetTimesheetByIdSuccess$(): void {
    this.actions.getTimesheetByIdSuccess$.subscribe({
      next: ({ timesheet }) => {
        this._timesheetState.set({
          timesheet: timesheet
        });
      }
    });
  }

  private _subscribeToCreateTimesheet$(): void {
    this.actions.onCreateTimesheet(
      (payload$) =>
        payload$.pipe(
          exhaustMap((payload) =>
            this._timesheetService.createTimesheet(payload.timesheet).pipe(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse)),
              map((response) => ({ activatedRoute: payload.activatedRoute, timesheet: response }))
            )
          )
        ),
      ({ activatedRoute, timesheet }) => {
        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.timesheet_created_successfully')
        });

        this.actions.createTimesheetSuccess({ timesheet });

        this._router
          .navigate(['../'], {
            relativeTo: activatedRoute,
            queryParams: activatedRoute.snapshot.queryParams
          })
          .then(() => {
            this._router
              .navigate([`../${timesheet.id}`], {
                relativeTo: activatedRoute,
                queryParams: activatedRoute.snapshot.queryParams
              })
              .then();
          });

        this.actions.getTimesheets({
          queryParams: activatedRoute.snapshot.queryParams
        });
      }
    );
  }

  private _subscribeToCreateTimesheetSuccess$(): void {
    this.actions.createTimesheetSuccess$.subscribe({
      next: ({ timesheet }) => {
        this._timesheetState.set({
          timesheet: timesheet
        });
      }
    });
  }

  private _subscribeToUpdateTimesheet$(): void {
    type APIResponse = UnwrapReturnObs<typeof this._timesheetService.updateTimesheet>;

    this.actions.onUpdateTimesheet(
      (payload$) =>
        payload$.pipe(
          exhaustMap((payload) =>
            this._timesheetService.updateTimesheet(payload.id, payload.timesheet).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        ),
      (timesheet) => {
        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.timesheet_updated_successfully')
        });

        //TODO: Currently, does not return the new timesheet
        this.actions.updateTimesheetSuccess({ timesheet: timesheet });
      }
    );
  }

  private _subscribeToUpdateTimesheetSuccess$(): void {
    this.actions.updateTimesheetSuccess$.subscribe({
      next: ({ timesheet }) => {
        this._timesheetState.set({
          timesheets: this._timesheetState.timesheets().map((item) => (item.id == timesheet.id ? timesheet : item)),
          timesheet: this._timesheetState.timesheet()?.id == timesheet.id ? timesheet : this._timesheetState.timesheet()
        });
      }
    });
  }

  private _subscribeToDeleteTimesheet$(): void {
    this.actions.onDeleteTimesheet(
      (payload$) =>
        payload$.pipe(
          exhaustMap((payload) =>
            this._timesheetService.deleteTimesheet(payload.id).pipe(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse)),
              filter((isDeleted) => isDeleted),
              map(() => ({ activatedRoute: payload.activatedRoute }))
            )
          )
        ),
      ({ activatedRoute }) => {
        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.timesheet_deleted_successfully')
        });

        this._router
          .navigate(['id' in activatedRoute.snapshot.params ? '../' : './'], {
            relativeTo: activatedRoute,
            queryParams: activatedRoute.snapshot.queryParams
          })
          .then();

        this.actions.getTimesheets({
          queryParams: activatedRoute.snapshot.queryParams
        });

        this.actions.deleteTimesheetSuccess();
      }
    );
  }

  private _subscribeToGetTimesheetEventItems$(): void {
    type APIResponse = UnwrapReturnObs<typeof this._timesheetService.getTimesheetEventItems>;

    this.actions.onGetTimesheetEventItems(
      (payload$) =>
        payload$.pipe(
          exhaustMap((payload) =>
            this._timesheetService.getTimesheetEventItems(payload?.queryParams).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        ),
      ({ pagination, data }) => {
        this.actions.getTimesheetEventItemsSuccess({
          pagination: pagination,
          items: data
        });
      }
    );
  }

  private _subscribeToGetTimesheetEventItemsSuccess$(): void {
    this.actions.getTimesheetEventItemsSuccess$.subscribe({
      next: ({ pagination, items }) => {
        this._timesheetState.set({
          timesheetEventItemsPagination: pagination,
          timesheetEventItems: items
        });
      }
    });
  }

  private _subscribeToUpdateTimesheetEvents$(): void {
    type APIResponse = UnwrapReturnObs<typeof this._timesheetService.updateTimesheetEvents>;

    this.actions.onUpdateTimesheetEvents(
      (payload$) =>
        payload$.pipe(
          exhaustMap((payload) =>
            this._timesheetService.updateTimesheetEvents(payload.id, payload.timesheet).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        ),
      (timesheet) => {
        this._popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.timesheet_events_updated_successfully')
        });

        //TODO: Currently, does not return the new timesheet
        this.actions.updateTimesheetEventsSuccess({ timesheet: timesheet });
      }
    );
  }

  private _subscribeToUpdateTimesheetEventsSuccess$(): void {
    this.actions.updateTimesheetEventsSuccess$.subscribe({
      next: ({ timesheet }) => {
        this._timesheetState.set({
          timesheets: this._timesheetState.timesheets().map((item) => (item.id == timesheet.id ? timesheet : item)),
          timesheet: this._timesheetState.timesheet()?.id == timesheet.id ? timesheet : this._timesheetState.timesheet()
        });
      }
    });
  }

  private _subscribeToSelectDateFilter$(): void {
    this.actions.onSelectDateFilter((payload$) =>
      payload$.pipe(
        tap((payload) => {
          this._timesheetState.set({
            dateFilter: payload.type
          });
        })
      )
    );
  }
}
